import { api } from "@/lib/apiClient";
import {
  effectiveBelegdatum,
  formatBelegdatum,
  getDokumentTypLabel,
  type DokumentDetail,
  type DokumentVerknuepfung,
} from "@/lib/dokumente";
import { stripGdRichTextMarkup } from "@/lib/gdRichText";

export interface GdPlaceholderItem {
  token: string;
  label: string;
}

export interface GdPlaceholderCategory {
  key: string;
  label: string;
  items: GdPlaceholderItem[];
}

let cachedCatalog: GdPlaceholderCategory[] | null = null;
let inflight: Promise<GdPlaceholderCategory[]> | null = null;

/** Statischer Katalog als Fallback – muss zu api/gd_placeholders.py passen. */
export const GD_PLACEHOLDER_CATALOG_FALLBACK: GdPlaceholderCategory[] = [
  {
    key: "firma",
    label: "Firma",
    items: [
      { token: "{{Firma.name}}", label: "Firmenname" },
      { token: "{{Firma.strasse}}", label: "Straße" },
      { token: "{{Firma.plz}}", label: "PLZ" },
      { token: "{{Firma.ort}}", label: "Ort" },
      { token: "{{Firma.anschrift}}", label: "Ganze Anschrift" },
      { token: "{{Firma.ust_id}}", label: "Umsatzsteuer-IdNr." },
      { token: "{{Firma.kontoinhaber}}", label: "Kontoinhaber" },
      { token: "{{Firma.bank}}", label: "Kreditinstitut" },
      { token: "{{Firma.iban}}", label: "IBAN" },
      { token: "{{Firma.bic}}", label: "BIC" },
    ],
  },
  {
    key: "kunde",
    label: "Kunde",
    items: [
      { token: "{{Kunde.name}}", label: "Kundenname / Firma" },
      { token: "{{Kunde.stamm_name}}", label: "Kundenstamm-Name" },
      { token: "{{Kunde.nr}}", label: "Kundennummer" },
      { token: "{{Kunde.email}}", label: "E-Mail" },
      { token: "{{Kunde.telefon}}", label: "Telefon" },
    ],
  },
  {
    key: "kundenadresse",
    label: "Kundenadresse",
    items: [
      { token: "{{Kunde.adresszusatz}}", label: "Adresszusatz" },
      { token: "{{Kunde.strasse}}", label: "Straße" },
      { token: "{{Kunde.hausnummer}}", label: "Hausnummer" },
      { token: "{{Kunde.strasse_hausnummer}}", label: "Straße Hausnummer" },
      { token: "{{Kunde.plz}}", label: "PLZ" },
      { token: "{{Kunde.ort}}", label: "Ort" },
      { token: "{{Kunde.plz_ort}}", label: "PLZ Ort" },
      { token: "{{Kunde.anschrift}}", label: "Vollständige Anschrift" },
      { token: "{{Kunde.maps_link}}", label: "Link Google Maps" },
    ],
  },
  {
    key: "ansprechpartner",
    label: "Ansprechpartner",
    items: [
      {
        token: "{{Ansprechpartner.briefanrede}}",
        label: "Briefanrede (Sehr geehrter Herr …,)",
      },
      { token: "{{Ansprechpartner.anrede_titel}}", label: "Anrede (Herr/Frau …)" },
      { token: "{{Ansprechpartner.vorname}}", label: "Vorname" },
      { token: "{{Ansprechpartner.nachname}}", label: "Nachname" },
      { token: "{{Ansprechpartner.name}}", label: "Ganzer Name" },
      { token: "{{Ansprechpartner.anrede}}", label: "z. Hd. …" },
      { token: "{{Ansprechpartner.email}}", label: "E-Mail" },
      { token: "{{Ansprechpartner.telefon}}", label: "Telefon" },
    ],
  },
  {
    key: "projekt",
    label: "Projekt",
    items: [
      { token: "{{Projekt.nummer}}", label: "Projektnummer" },
      { token: "{{Projekt.name}}", label: "Projektname" },
      { token: "{{Projekt.beschreibung}}", label: "Projektbeschreibung" },
      { token: "{{Projekt.ort}}", label: "Projektort" },
      { token: "{{Projekt.strasse}}", label: "Straße" },
      { token: "{{Projekt.plz}}", label: "PLZ" },
      { token: "{{Projekt.anschrift}}", label: "Projektadresse" },
      { token: "{{Projekt.maps_link}}", label: "Link Google Maps" },
      { token: "{{Projekt.bestellnummer}}", label: "Bestellnummer" },
      { token: "{{Projekt.kundenreferenz}}", label: "Kundenreferenz" },
      { token: "{{Projekt.leitweg_id}}", label: "Leitweg-ID" },
    ],
  },
  {
    key: "dokument",
    label: "Dokument",
    items: [
      { token: "{{Dokument.typ}}", label: "Dokumenttyp" },
      { token: "{{Dokument.nummer}}", label: "Dokument-Nr." },
      { token: "{{Dokument.betreff}}", label: "Betreff" },
      { token: "{{Dokument.belegdatum}}", label: "Belegdatum" },
      { token: "{{Dokument.gueltig_bis}}", label: "Gültig bis" },
      { token: "{{Dokument.leistungsdatum}}", label: "Leistungsdatum" },
      { token: "{{Dokument.lieferdatum}}", label: "Lieferdatum" },
      { token: "{{Dokument.faelligkeitsdatum}}", label: "Fälligkeitsdatum" },
      { token: "{{Dokument.bestellnummer}}", label: "Bestellnummer" },
      { token: "{{Dokument.leitweg_id}}", label: "Leitweg-ID" },
      { token: "{{Dokument.aktuelles_datum}}", label: "Aktuelles Datum" },
      { token: "{{Dokument.status}}", label: "Status" },
    ],
  },
  {
    key: "zahlung",
    label: "Zahlung",
    items: [
      { token: "{{Zahlung.zahlungsart}}", label: "Zahlungsart" },
      { token: "{{Zahlung.zahlungsziel_tage}}", label: "Zahlungsziel Tage" },
      { token: "{{Zahlung.zahlungsziel_datum}}", label: "Zahlungsziel Datum" },
      { token: "{{Zahlung.skonto_prozent}}", label: "Skonto %" },
      { token: "{{Zahlung.skontoziel_tage}}", label: "Skontoziel Tage" },
      { token: "{{Zahlung.skontoziel_datum}}", label: "Skontoziel Datum" },
    ],
  },
  {
    key: "ersteller",
    label: "Ersteller",
    items: [
      { token: "{{Ersteller.name}}", label: "Name" },
      { token: "{{Ersteller.telefon}}", label: "Telefon" },
      { token: "{{Ersteller.mobil}}", label: "Mobil" },
      { token: "{{Ersteller.email}}", label: "E-Mail" },
    ],
  },
  {
    key: "summen",
    label: "Summen",
    items: [
      { token: "{{Summe.netto}}", label: "Betrag (Netto)" },
      { token: "{{Summe.brutto}}", label: "Betrag (Brutto)" },
      { token: "{{Summe.mwst}}", label: "MwSt.-Betrag" },
      { token: "{{Summe.bezahlt}}", label: "Bezahlt" },
      { token: "{{Summe.offen}}", label: "Offener Betrag" },
      { token: "{{Summe.waehrung}}", label: "Währung" },
    ],
  },
    {
      key: "referenzdokument",
      label: "Referenzdokument",
      items: [
        { token: "{{Referenzdokument.nummer}}", label: "Dokument-Nr." },
        { token: "{{Referenzdokument.typ}}", label: "Dokumenttyp" },
      ],
    },
    {
      key: "mahnung",
      label: "Mahnung",
      items: [
        { token: "{{Mahnung.stufe}}", label: "Mahnstufe (z. B. 1. Mahnung)" },
        { token: "{{Mahnung.nummer}}", label: "Mahnungsnummer" },
        { token: "{{Mahnung.datum}}", label: "Mahndatum" },
        { token: "{{Mahnung.zahlungsfrist}}", label: "Neue Zahlungsfrist (Datum)" },
        { token: "{{Mahnung.zahlungsfrist_tage}}", label: "Zahlungsfrist in Tagen" },
        { token: "{{Mahnung.hauptforderung}}", label: "Offene Hauptforderung" },
        { token: "{{Mahnung.zinsen}}", label: "Verzugszinsen" },
        { token: "{{Mahnung.kosten}}", label: "Mahnkosten/Nebenforderungen" },
        { token: "{{Mahnung.gesamtforderung}}", label: "Gesamtforderung" },
        { token: "{{Mahnung.rechnungsnummern}}", label: "Rechnungsnummer(n)" },
        { token: "{{Mahnung.rechnung_nummer}}", label: "Erste Rechnungsnummer" },
        { token: "{{Mahnung.anzahl_rechnungen}}", label: "Anzahl Rechnungen" },
      ],
    },
    {
      key: "mail",
      label: "E-Mail",
      items: [
        {
          token: "{{Signatur}}",
          label: "Firmensignatur (aus Kommunikationseinstellungen)",
        },
      ],
    },
  ];

export async function fetchGdPlaceholderCatalog(): Promise<GdPlaceholderCategory[]> {
  if (cachedCatalog) return cachedCatalog;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const data = await api.get<{ categories?: GdPlaceholderCategory[] }>("/dokumente/platzhalter");
      cachedCatalog = data?.categories?.length ? data.categories : GD_PLACEHOLDER_CATALOG_FALLBACK;
    } catch {
      cachedCatalog = GD_PLACEHOLDER_CATALOG_FALLBACK;
    } finally {
      inflight = null;
    }
    return cachedCatalog;
  })();
  return inflight;
}

function moneyDe(value: number, currency: string): string {
  return `${value.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`.trim();
}

function mapsLink(...parts: Array<string | null | undefined>): string {
  const query = parts.map((p) => (p || "").trim()).filter(Boolean).join(", ");
  if (!query) return "";
  return `https://maps.google.com/?q=${encodeURIComponent(query)}`;
}

function dateLabel(value?: string | null): string {
  const formatted = formatBelegdatum(value);
  return formatted === "—" ? "" : formatted;
}

/** Formale Briefanrede inkl. geehrter/geehrte. */
export function buildBriefanrede(options: {
  anrede?: string | null;
  nachname?: string | null;
  vorname?: string | null;
  vollerName?: string | null;
}): string {
  let rawAnrede = (options.anrede || "").trim().toLowerCase().replace(/\.$/, "");
  let last = (options.nachname || "").trim();
  let first = (options.vorname || "").trim();
  const full = (options.vollerName || "").trim();

  if (!last && full) {
    let rest = full;
    for (const prefix of ["herr ", "frau ", "hr ", "fr ", "hr. ", "fr. "]) {
      if (rest.toLowerCase().startsWith(prefix)) {
        rest = rest.slice(prefix.length).trim();
        if (!rawAnrede) rawAnrede = prefix.trim().replace(/\.$/, "").toLowerCase();
        break;
      }
    }
    const parts = rest.split(/\s+/).filter(Boolean);
    if (parts.length) {
      last = parts[parts.length - 1] || "";
      if (!first && parts.length > 1) first = parts.slice(0, -1).join(" ");
    }
  }

  const isFrau = ["frau", "fr", "mrs", "ms", "miss"].includes(rawAnrede);
  const isHerr = ["herr", "hr", "mr"].includes(rawAnrede);
  if (isFrau && last) return `Sehr geehrte Frau ${last},`;
  if (isHerr && last) return `Sehr geehrter Herr ${last},`;
  return "Sehr geehrte Damen und Herren,";
}

function resolveReferenz(
  docId: number | undefined,
  verknuepfungen?: DokumentVerknuepfung[] | null,
): { nummer: string; typ: string } {
  if (!docId || !verknuepfungen?.length) return { nummer: "", typ: "" };
  const incoming = verknuepfungen.filter((v) => v.ziel_dokument_id === docId);
  const explicit = incoming.filter((v) => v.verknuepfung_typ === "referenz");
  const chosen = explicit.length
    ? explicit
    : incoming.filter((v) => v.verknuepfung_typ === "umwandlung" && v.quell_nummer);
  const nums = chosen
    .map((v) => (v.quell_nummer || "").trim())
    .filter((n, idx, all) => n && all.indexOf(n) === idx);
  if (!nums.length) return { nummer: "", typ: "" };
  return {
    nummer: nums.join(", "),
    typ: chosen.length === 1 ? (chosen[0]?.quell_typ || "").trim() : "referenz",
  };
}

type PlaceholderDoc = Pick<
  DokumentDetail,
  | "id"
  | "kunde_name"
  | "kunde_stamm_name"
  | "kunde_email"
  | "kunde_telefon"
  | "kunde_adresszusatz"
  | "kunde_strasse"
  | "kunde_hausnummer"
  | "kunde_plz"
  | "kunde_ort"
  | "kundennummer"
  | "ansprechpartner_name"
  | "ansprechpartner_email"
  | "ansprechpartner_telefon"
  | "ansprechpartner_anrede"
  | "ansprechpartner_vorname"
  | "ansprechpartner_nachname"
  | "auftrag_nummer"
  | "auftrag_ort"
  | "auftrag_beschreibung"
  | "auftrag_strasse"
  | "auftrag_plz"
  | "auftrag_bestellnummer"
  | "auftrag_kundenreferenz"
  | "auftrag_leitweg_id"
  | "leitweg_id"
  | "dokument_typ"
  | "rechnung_art"
  | "dokument_nummer"
  | "betreff"
  | "belegdatum"
  | "gueltig_bis"
  | "leistungsdatum"
  | "lieferdatum"
  | "faelligkeitsdatum"
  | "bestellnummer"
  | "status"
  | "created_by_name"
  | "created_by_telefon"
  | "created_by_mobil"
  | "created_by_email"
  | "waehrung"
  | "summe_netto"
  | "summe_steuer"
  | "summe_brutto"
  | "summe_bezahlt"
  | "summe_offen"
  | "verknuepfungen"
> & {
  projekt_name?: string | null;
  referenz_dokument_nummer?: string | null;
  referenz_dokument_typ?: string | null;
  referenzdokument_anzeigen?: boolean;
};

export function buildGdPlaceholderValues(doc: PlaceholderDoc): Record<string, string> {
  const currency = (doc.waehrung || "EUR").trim() || "EUR";
  const street = (doc.kunde_strasse || "").trim();
  const hausnr = (doc.kunde_hausnummer || "").trim();
  const streetFull = [street, hausnr].filter(Boolean).join(" ");
  const plz = (doc.kunde_plz || "").trim();
  const ort = (doc.kunde_ort || "").trim();
  const plzOrt = [plz, ort].filter(Boolean).join(" ");
  const apName = (doc.ansprechpartner_name || "").trim();
  const apAnrede = (doc.ansprechpartner_anrede || "").trim();
  const apVorname = (doc.ansprechpartner_vorname || "").trim();
  const apNachname = (doc.ansprechpartner_nachname || "").trim();
  // Rechnungen: kein „z. Hd.“ in der Anschrift – der Ansprechpartner steht im Infoblock.
  const address = [
    (doc.kunde_name || "").trim(),
    (doc.kunde_adresszusatz || "").trim(),
    apName && doc.dokument_typ !== "rechnung" ? `z. Hd. ${apName}` : "",
    streetFull,
    plzOrt,
  ].filter(Boolean);

  const projektStrasse = (doc.auftrag_strasse || "").trim();
  const projektPlz = (doc.auftrag_plz || "").trim();
  const projektOrt = (doc.auftrag_ort || "").trim();
  const projektPlzOrt = [projektPlz, projektOrt].filter(Boolean).join(" ");
  const projektAnschrift = [projektStrasse, projektPlzOrt].filter(Boolean).join("\n");
  const projektName = (
    doc.projekt_name ||
    doc.betreff ||
    doc.auftrag_beschreibung ||
    ""
  ).trim();

  const showRef = doc.referenzdokument_anzeigen !== false;
  const ref = !showRef
    ? { nummer: "", typ: "" }
    : doc.referenz_dokument_nummer != null
      ? {
          nummer: (doc.referenz_dokument_nummer || "").trim(),
          typ: (doc.referenz_dokument_typ || "").trim(),
        }
      : resolveReferenz(doc.id, doc.verknuepfungen);

  const values: Record<string, string> = {
    "Kunde.name": (doc.kunde_name || "").trim(),
    "Kunde.stamm_name": (doc.kunde_stamm_name || "").trim(),
    "Kunde.email": (doc.kunde_email || "").trim(),
    "Kunde.telefon": (doc.kunde_telefon || "").trim(),
    "Kunde.nr": (doc.kundennummer || "").trim(),
    "Kunde.adresszusatz": (doc.kunde_adresszusatz || "").trim(),
    "Kunde.strasse": street,
    "Kunde.hausnummer": hausnr,
    "Kunde.strasse_hausnummer": streetFull,
    "Kunde.plz": plz,
    "Kunde.ort": ort,
    "Kunde.plz_ort": plzOrt,
    "Kunde.anschrift": address.join("\n"),
    "Kunde.maps_link": mapsLink(streetFull, plzOrt),
    "Ansprechpartner.anrede_titel": apAnrede,
    "Ansprechpartner.vorname": apVorname,
    "Ansprechpartner.nachname": apNachname,
    "Ansprechpartner.name": apName,
    "Ansprechpartner.briefanrede": buildBriefanrede({
      anrede: apAnrede,
      nachname: apNachname,
      vorname: apVorname,
      vollerName: apName,
    }),
    "Ansprechpartner.email": (doc.ansprechpartner_email || "").trim(),
    "Ansprechpartner.telefon": (doc.ansprechpartner_telefon || "").trim(),
    "Ansprechpartner.anrede": apName ? `z. Hd. ${apName}` : "",
    "Projekt.nummer": (doc.auftrag_nummer || "").trim(),
    "Projekt.name": stripGdRichTextMarkup(projektName).trim(),
    "Projekt.ort": projektOrt,
    "Projekt.strasse": projektStrasse,
    "Projekt.plz": projektPlz,
    "Projekt.beschreibung": (doc.auftrag_beschreibung || "").trim(),
    "Projekt.anschrift": projektAnschrift,
    "Projekt.maps_link": mapsLink(projektStrasse, projektPlzOrt),
    "Projekt.bestellnummer": (doc.bestellnummer || doc.auftrag_bestellnummer || "").trim(),
    "Projekt.kundenreferenz": (doc.auftrag_kundenreferenz || "").trim(),
    "Projekt.leitweg_id": (doc.leitweg_id || doc.auftrag_leitweg_id || "").trim(),
    "Dokument.typ": getDokumentTypLabel(doc),
    "Dokument.nummer": (doc.dokument_nummer || "").trim() || "Entwurf",
    "Dokument.betreff": stripGdRichTextMarkup(doc.betreff || "").trim(),
    "Dokument.belegdatum": dateLabel(effectiveBelegdatum(doc)),
    "Dokument.gueltig_bis": dateLabel(doc.gueltig_bis),
    "Dokument.leistungsdatum": dateLabel(doc.leistungsdatum),
    "Dokument.lieferdatum": dateLabel(doc.lieferdatum),
    "Dokument.faelligkeitsdatum": dateLabel(doc.faelligkeitsdatum),
    "Dokument.bestellnummer": (doc.bestellnummer || doc.auftrag_bestellnummer || "").trim(),
    "Dokument.leitweg_id": (doc.leitweg_id || doc.auftrag_leitweg_id || "").trim(),
    "Dokument.aktuelles_datum": new Date().toLocaleDateString("de-DE"),
    "Dokument.status": (doc.status || "").trim(),
    "Ersteller.name": (doc.created_by_name || "").trim(),
    "Ersteller.telefon": (doc.created_by_telefon || "").trim(),
    "Ersteller.mobil": (doc.created_by_mobil || "").trim(),
    "Ersteller.email": (doc.created_by_email || "").trim(),
    "Summe.netto": moneyDe(Number(doc.summe_netto || 0), currency),
    "Summe.brutto": moneyDe(Number(doc.summe_brutto || 0), currency),
    "Summe.mwst": moneyDe(Number(doc.summe_steuer || 0), currency),
    "Summe.bezahlt":
      doc.summe_bezahlt != null ? moneyDe(Number(doc.summe_bezahlt || 0), currency) : "",
    "Summe.offen":
      doc.summe_offen != null ? moneyDe(Number(doc.summe_offen || 0), currency) : "",
    "Summe.waehrung": currency,
    "Firma.name": "",
    "Firma.strasse": "",
    "Firma.plz": "",
    "Firma.ort": "",
    "Firma.anschrift": "",
    "Firma.ust_id": "",
    "Firma.kontoinhaber": "",
    "Firma.iban": "",
    "Firma.bic": "",
    "Firma.bank": "",
    "Zahlung.zahlungsart": "",
    "Zahlung.zahlungsziel_tage": "",
    "Zahlung.zahlungsziel_datum": dateLabel(doc.faelligkeitsdatum),
    "Zahlung.skonto_prozent": "",
    "Zahlung.skontoziel_tage": "",
    "Zahlung.skontoziel_datum": "",
    "Referenzdokument.nummer": ref.nummer,
    "Referenzdokument.typ": ref.typ,
  };
  return values;
}

const PLACEHOLDER_RE = /\{\{\s*([A-Za-zÄÖÜäöü0-9_.]+)\s*\}\}/g;

export function resolveGdPlaceholders(
  text: string | null | undefined,
  doc: Parameters<typeof buildGdPlaceholderValues>[0],
): string {
  const raw = text ?? "";
  if (!raw.includes("{{")) return raw;
  const values = buildGdPlaceholderValues(doc);
  const lowerMap: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) {
    lowerMap[k.toLowerCase()] = v;
  }
  return raw.replace(PLACEHOLDER_RE, (full, key: string): string => {
    const direct = values[key];
    if (direct !== undefined) return direct;
    const lower = lowerMap[key.toLowerCase()];
    return lower !== undefined ? lower : full;
  });
}

export function appendTextSnippet(current: string, snippet: string, asBlock = false): string {
  const base = current ?? "";
  const add = snippet ?? "";
  if (!add) return base;
  if (!base.trim()) return add;
  if (asBlock) {
    const sep = base.endsWith("\n") ? "\n" : "\n\n";
    return `${base}${sep}${add}`;
  }
  const needsSpace = !/\s$/.test(base) && !/^\s/.test(add);
  return `${base}${needsSpace ? " " : ""}${add}`;
}
